import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Leaf, Loader2, RefreshCw, Printer } from '../lib/icons';
import { lirePortail } from '../lib/portail';

const fmt = (v, d = 0) =>
  new Intl.NumberFormat('fr-FR', { minimumFractionDigits: d, maximumFractionDigits: d }).format(Number(v) || 0);
const heure = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const PAIEMENT_STYLE = {
  impaye: 'font-bold text-orange-300',
  solde: 'font-bold text-emerald-300',
  depot: 'font-bold text-sky-300',
  attente: 'text-amber-200',
};

export default function PortailCompte() {
  const { token } = useParams();
  const [doc, setDoc] = useState(undefined);
  const [rafraichi, setRafraichi] = useState(null);

  // Lien permanent : les données sont relues à chaque visite et toutes les 30 s.
  const charger = useCallback(async () => {
    const d = await lirePortail(token);
    setDoc(d ?? null);
    setRafraichi(new Date());
  }, [token]);

  useEffect(() => {
    charger();
    const t = setInterval(charger, 30000);
    const surFocus = () => document.visibilityState === 'visible' && charger();
    document.addEventListener('visibilitychange', surFocus);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', surFocus);
    };
  }, [charger]);

  if (doc === undefined)
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0d1f16] text-emerald-50">
        <Loader2 className="animate-spin" />
      </div>
    );

  if (!doc)
    return (
      <div data-testid="portail-introuvable" className="flex min-h-screen flex-col items-center justify-center gap-2 bg-[#0d1f16] px-6 text-center text-emerald-50">
        <p className="font-heading text-xl font-black">Lien introuvable</p>
        <p className="text-sm text-emerald-200/80">Cet espace personnel n'est plus accessible. Contactez l'huilerie.</p>
      </div>
    );

  const p = doc.payload;
  // Ordre chronologique strict : la plus récente en haut, la plus ancienne en bas.
  const lignes = [...(p.lignes ?? [])]
    .map((l, i) => ({ l, i }))
    .sort((a, b) => String(b.l.date ?? '').localeCompare(String(a.l.date ?? '')) || b.i - a.i)
    .map(({ l }) => l);
  const voyages = p.voyages ?? [];
  const factures = p.factures ?? [];
  const avances = p.avances ?? [];
  const resteAPayer = Number(p.resteAPayer ?? 0);

  return (
    <div data-testid="portail-compte" className="min-h-screen bg-[#0d1f16] px-4 py-8 text-emerald-50">
      <div className="mx-auto w-full max-w-3xl space-y-6">
        <header className="flex items-center justify-between gap-3 border-b border-emerald-800/60 pb-5">
          <div className="flex items-center gap-3">
            {p.logo && <img src={p.logo} alt="Logo" className="h-14 w-14 rounded bg-white/95 object-contain p-1" />}
            <div>
              <p className="font-heading text-lg font-black leading-tight">{p.huilerie}</p>
              <p className="font-data text-[11px] uppercase tracking-[0.14em] text-emerald-300">Espace partenaire — lecture seule</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <a
              data-testid="portail-pdf"
              href={`${process.env.REACT_APP_BACKEND_URL}/api/portail/${token}/pdf`}
              target="_blank"
              rel="noreferrer"
              className="flex h-10 items-center gap-2 border border-emerald-500 bg-emerald-600/20 px-3 text-xs font-bold text-emerald-100 transition-colors duration-150 hover:bg-emerald-700"
            >
              <Printer size={13} /> Imprimer / Télécharger le relevé PDF
            </a>
            <button
              type="button"
              data-testid="portail-actualiser"
              onClick={charger}
              className="flex h-10 items-center gap-2 border border-emerald-700 px-3 text-xs font-bold text-emerald-100 transition-colors duration-150 hover:bg-emerald-800"
            >
              <RefreshCw size={13} /> Actualiser
            </button>
          </div>
        </header>

        <section className="border border-emerald-800/60 bg-emerald-950/40 p-5">
          <p className="font-data text-[11px] uppercase tracking-[0.14em] text-emerald-300">Titulaire du compte</p>
          <p data-testid="portail-compte-nom" className="font-heading text-2xl font-black tracking-tight">{p.nom}</p>
          <p className="font-data text-sm text-emerald-100/80">
            {p.code && <span data-testid="portail-code" className="mr-3 font-bold text-emerald-300">{p.code}</span>}
            {p.tel}
          </p>
        </section>

        <section className="grid grid-cols-1 gap-px bg-emerald-800/60 sm:grid-cols-3">
          <div className="bg-[#0d1f16] px-4 py-4">
            <p className="text-[11px] uppercase tracking-[0.08em] text-emerald-400">Solde financier net</p>
            <p data-testid="portail-solde" className="font-data text-2xl font-semibold">{fmt(Math.abs(p.solde), 3)} TND</p>
            <p className="text-[11px] text-emerald-200/80">
              {p.solde > 0 ? 'L’huilerie vous doit ce montant' : p.solde < 0 ? 'Vous devez ce montant à l’huilerie' : 'Compte soldé'}
            </p>
          </div>
          <div className="bg-[#0d1f16] px-4 py-4">
            <p className="text-[11px] uppercase tracking-[0.08em] text-emerald-400">Huile en dépôt à l'huilerie</p>
            <p data-testid="portail-stock-huile" className="font-data text-2xl font-semibold">{fmt(p.stockHuile, 1)} Kg</p>
            <p className="text-[11px] text-emerald-200/80">Déposée {fmt(p.depose, 1)} · retirée {fmt(p.retire, 1)} Kg</p>
          </div>
          <div className="bg-[#0d1f16] px-4 py-4">
            <p className="text-[11px] uppercase tracking-[0.08em] text-emerald-400">Voyages / réceptions</p>
            <p data-testid="portail-nb-voyages" className="font-data text-2xl font-semibold">{fmt(voyages.length)}</p>
            <p className="text-[11px] text-emerald-200/80">{fmt(voyages.reduce((s, v) => s + (v.poidsNet || 0), 0))} Kg d'olives · {fmt(p.lignes.length)} opérations</p>
          </div>
        </section>

        {p.avanceDisponible > 0 && (
          <section data-testid="portail-avance" className="border border-amber-700/60 bg-amber-950/30 px-4 py-3">
            <p className="text-[11px] uppercase tracking-[0.08em] text-amber-300">Avance disponible chez l'huilerie</p>
            <p className="font-data text-xl font-semibold text-amber-100">{fmt(p.avanceDisponible, 3)} TND</p>
            <p className="text-[11px] text-amber-200/80">Sera déduite automatiquement de vos prochains bons de réception.</p>
          </section>
        )}

        {(factures.length > 0 || avances.length > 0) && (
          <section data-testid="portail-fournisseur" className="space-y-4">
            <div className="grid grid-cols-1 gap-px bg-emerald-800/60 sm:grid-cols-3">
              <div className="bg-[#0d1f16] px-4 py-4">
                <p className="text-[11px] uppercase tracking-[0.08em] text-emerald-400">Reste à payer (factures)</p>
                <p data-testid="portail-reste-a-payer" className="font-data text-2xl font-semibold text-orange-200">{fmt(resteAPayer, 3)} TND</p>
                <p className="text-[11px] text-emerald-200/80">{factures.filter((f) => f.reste > 0.001).length} facture(s) non soldée(s)</p>
              </div>
              <div className="bg-[#0d1f16] px-4 py-4">
                <p className="text-[11px] uppercase tracking-[0.08em] text-emerald-400">Avances reçues</p>
                <p data-testid="portail-total-avances" className="font-data text-2xl font-semibold">{fmt(avances.reduce((s, a) => s + a.montant, 0), 3)} TND</p>
                <p className="text-[11px] text-emerald-200/80">dont {fmt(p.avanceCreditee ?? 0, 3)} TND encore à imputer</p>
              </div>
              <div className="bg-[#0d1f16] px-4 py-4">
                <p className="text-[11px] uppercase tracking-[0.08em] text-emerald-400">Total facturé</p>
                <p className="font-data text-2xl font-semibold">{fmt(factures.reduce((s, f) => s + f.montantTTC, 0), 3)} TND</p>
                <p className="text-[11px] text-emerald-200/80">{factures.length} facture(s) enregistrée(s)</p>
              </div>
            </div>

            {factures.length > 0 && (
              <div className="border border-emerald-800/60">
                <p className="border-b border-emerald-800/60 px-4 py-3 font-heading text-sm font-bold">Vos factures</p>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-left" data-testid="portail-factures">
                    <thead>
                      <tr className="bg-emerald-950/60">
                        {['Date', 'N° facture', 'Objet', 'HT', 'TVA', 'TTC', 'Retenue', 'Reste à payer', 'Statut'].map((h) => (
                          <th key={h} className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.08em] text-emerald-300">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {factures.map((f) => (
                        <tr key={f.numero} data-testid={`portail-facture-${f.numero}`} className="border-b border-emerald-900/60">
                          <td className="px-3 py-2 font-data text-xs">{f.date}</td>
                          <td className="px-3 py-2 font-data text-xs font-semibold">{f.numeroFacture}</td>
                          <td className="px-3 py-2 text-xs">{f.libelle || f.categorie}</td>
                          <td className="px-3 py-2 font-data text-xs">{fmt(f.montantHT, 3)}</td>
                          <td className="px-3 py-2 font-data text-xs">{fmt(f.montantTVA, 3)}</td>
                          <td className="px-3 py-2 font-data text-xs font-semibold">{fmt(f.montantTTC, 3)}</td>
                          <td className="px-3 py-2 font-data text-xs">{f.montantRS ? `−${fmt(f.montantRS, 3)}` : '—'}</td>
                          <td className={`px-3 py-2 font-data text-xs font-bold ${f.reste > 0.001 ? 'text-orange-300' : 'text-emerald-300'}`}>{fmt(f.reste, 3)}</td>
                          <td className="px-3 py-2 text-xs">{f.statut}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {avances.length > 0 && (
              <div className="border border-emerald-800/60">
                <p className="border-b border-emerald-800/60 px-4 py-3 font-heading text-sm font-bold">Avances versées par l'huilerie</p>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-left" data-testid="portail-avances">
                    <thead>
                      <tr className="bg-emerald-950/60">
                        {['Date', 'Réf.', 'Mode', 'N° pièce', 'Montant', 'Imputé', 'Solde créditeur'].map((h) => (
                          <th key={h} className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.08em] text-emerald-300">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {avances.map((a) => (
                        <tr key={a.numero} data-testid={`portail-avance-${a.numero}`} className="border-b border-emerald-900/60">
                          <td className="px-3 py-2 font-data text-xs">{a.date}</td>
                          <td className="px-3 py-2 font-data text-xs font-semibold">{a.numero}</td>
                          <td className="px-3 py-2 text-xs">{a.mode}</td>
                          <td className="px-3 py-2 font-data text-xs">{a.piece || '—'}</td>
                          <td className="px-3 py-2 font-data text-xs font-semibold">{fmt(a.montant, 3)}</td>
                          <td className="px-3 py-2 font-data text-xs">{fmt(a.impute, 3)}</td>
                          <td className="px-3 py-2 font-data text-xs font-bold text-amber-200">{fmt(a.reste, 3)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>
        )}

        {voyages.length > 0 && (
          <section className="border border-emerald-800/60">
            <p className="border-b border-emerald-800/60 px-4 py-3 font-heading text-sm font-bold">Vos voyages, pesées & triturations</p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left" data-testid="portail-voyages">
                <thead>
                  <tr className="bg-emerald-950/60">
                    {['Date', 'Ticket', 'Type', 'Brut', 'Tare', 'Net (Kg)', 'Rdt %', 'Huile (Kg)', 'Destination', 'Étape', 'Paiement'].map((h) => (
                      <th key={h} className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.08em] text-emerald-300">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {voyages.map((v) => (
                    <tr key={v.id} data-testid={`portail-voyage-${v.id}`} className="border-b border-emerald-900/60">
                      <td className="px-3 py-2 font-data text-xs">{v.date}</td>
                      <td className="px-3 py-2 font-data text-xs font-semibold">{v.id}</td>
                      <td className="px-3 py-2 text-xs">{v.parcours === 'client' ? 'Trituration (prestation)' : `Vente d'olives · ${v.qualite}`}</td>
                      <td className="px-3 py-2 font-data text-xs">{fmt(v.poidsBrut)}</td>
                      <td className="px-3 py-2 font-data text-xs">{v.tare === null || v.tare === undefined ? 'en attente' : fmt(v.tare)}</td>
                      <td className="px-3 py-2 font-data text-xs font-semibold">{fmt(v.poidsNet)}</td>
                      <td className="px-3 py-2 font-data text-xs">{v.rendement ? fmt(v.rendement, 2) : '—'}</td>
                      <td className="px-3 py-2 font-data text-xs">{v.huile ? fmt(v.huile, 1) : '—'}</td>
                      <td className="px-3 py-2 text-xs">{v.devenir === 'cuve' ? `Dépôt · ${v.cuve ?? 'cuve'}` : v.devenir === 'sortie' ? 'Emportée' : '—'}</td>
                      <td className="px-3 py-2 text-xs">{v.statut}</td>
                      <td className="px-3 py-2 text-xs">
                        <span className={PAIEMENT_STYLE[v.paiement?.code] ?? 'text-emerald-200/70'}>{v.paiement?.label ?? '—'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        <section className="border border-emerald-800/60">
          <p className="border-b border-emerald-800/60 px-4 py-3 font-heading text-sm font-bold">Historique détaillé (plus récent en premier)</p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left" data-testid="portail-historique">
              <thead>
                <tr className="bg-emerald-950/60">
                  {['Date', 'Opération', 'N° Bon', 'Brut (Kg)', 'Net (Kg)', 'Rdt théorique', 'Prix base', 'Débit', 'Crédit', 'Solde', 'Notes'].map((h) => (
                    <th key={h} className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.08em] text-emerald-300">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lignes.map((l, i) => (
                  <tr key={`${l.id}-${i}`} className="border-b border-emerald-900/60">
                    <td className="px-3 py-2 font-data text-xs">{l.date}</td>
                    <td className="px-3 py-2 text-xs">{l.type}</td>
                    <td className="px-3 py-2 font-data text-xs font-semibold">{l.bon}</td>
                    <td className="px-3 py-2 font-data text-xs">{l.poidsBrut ? fmt(l.poidsBrut) : '—'}</td>
                    <td className="px-3 py-2 font-data text-xs">{l.poidsNet ? fmt(l.poidsNet) : '—'}</td>
                    <td data-testid={`portail-rdt-theo-${i}`} className="px-3 py-2 font-data text-xs">
                      {l.rendementTheorique ? `${fmt(l.rendementTheorique, 2)} %` : '—'}
                    </td>
                    <td className="px-3 py-2 font-data text-xs">{l.prixBase ? fmt(l.prixBase, 3) : '—'}</td>
                    <td className="px-3 py-2 font-data text-xs text-orange-300">{l.debit ? fmt(l.debit, 3) : '—'}</td>
                    <td className="px-3 py-2 font-data text-xs text-emerald-300">{l.credit ? fmt(l.credit, 3) : '—'}</td>
                    <td className="px-3 py-2 font-data text-xs font-semibold">{fmt(l.solde, 3)}</td>
                    <td className="px-3 py-2 text-xs text-emerald-200/70">{l.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <footer className="flex flex-wrap items-center gap-2 pt-2 text-[11px] text-emerald-300/70">
          <Leaf size={13} /> Relevé mis à jour par l'huilerie le {heure(doc.maj)} · consulté à {rafraichi ? heure(rafraichi) : '—'} · actualisation automatique toutes les 30 s
        </footer>
      </div>
    </div>
  );
}
